import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft, FileText } from "lucide-react";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import SearchPhoneInput from "../common/SearchPhoneInput";
import type {
  Constante,
  Doctor,
  Patient,
  ConstanteFormData,
} from "../../types";
import { VisitReason } from "../../types";

interface ConstanteFormBaseProps {
  mode: "create" | "update";
  role: "admin" | "doctor";
  initialData?: Constante | null;
  onSubmit: (data: ConstanteFormData) => Promise<void>;
  isSubmitting?: boolean;
}

// Mapping des motifs pour affichage
const reasonMap: { [key in VisitReason]: string } = {
  [VisitReason.GENERAL_CONSULTATION]: "Consultation générale",
  [VisitReason.PEDIATRIC_CONSULTATION]: "Consultation pédiatrique",
  [VisitReason.SPECIALIST_CONSULTATION]: "Consultation spécialisée",
  [VisitReason.RESPIRATORY_PHYSIOTHERAPY]: "Kinésithérapie respiratoire",
  [VisitReason.PRE_EMPLOYMENT_VISIT]: "Visite de pré-embauche",
  [VisitReason.GYNECOLOGY_CONSULTATION]: "Consultation gynécologique",
};

const ConstanteFormBase: React.FC<ConstanteFormBaseProps> = ({
  mode,
  role,
  initialData,
  onSubmit,
  isSubmitting = false,
}) => {
  const [constanteFormData, setConstanteFormData] = useState<ConstanteFormData>(
    {
      dossierMedicalId: null,
      poids: String(initialData?.poids || initialData?.weight || ""),
      taille: String(initialData?.taille || initialData?.height || ""),
      tensionArterielle: String(
        initialData?.tensionArterielle || initialData?.bloodPressure || ""
      ),
      frequenceCardiaque: String(
        initialData?.frequenceCardiaque ||
        initialData?.heartRate ||
        initialData?.rythmeCardiaque ||
        ""
      ),
      temperature: String(initialData?.temperature || ""),
      motif:
        (initialData?.motif as VisitReason | "") ||
        (initialData?.reason as VisitReason | "") ||
        "",
      doctorId: initialData?.doctorId?.toString() || "",
      maladeId:
        initialData?.maladeId?.toString() ||
        initialData?.patientId?.toString() ||
        "",
      status: initialData?.status || "PENDING",
    }
  );

  // Mettre à jour le formulaire quand initialData change (important pour l'asynchrone)
  useEffect(() => {
    if (initialData && mode === "update") {
      setConstanteFormData((prev) => ({
        ...prev,
        poids: String(initialData.poids || initialData.weight || ""),
        taille: String(initialData.taille || initialData.height || ""),
        tensionArterielle: String(
          initialData.tensionArterielle || initialData.bloodPressure || ""
        ),
        frequenceCardiaque: String(
          initialData.frequenceCardiaque ||
          initialData.heartRate ||
          initialData.rythmeCardiaque ||
          ""
        ),
        temperature: String(initialData.temperature || ""),
        motif:
          (initialData.motif as VisitReason | "") ||
          (initialData.reason as VisitReason | "") ||
          "",
        doctorId: initialData.doctorId?.toString() || prev.doctorId,
        maladeId:
          initialData.maladeId?.toString() ||
          initialData.patientId?.toString() ||
          prev.maladeId,
        status: initialData.status || prev.status,
      }));
    }
  }, [initialData, mode]);

  // États pour la gestion des données
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [searchPhone, setSearchPhone] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [selectedPatientDOB, setSelectedPatientDOB] = useState("");
  const [selectedPatientPhone, setSelectedPatientPhone] = useState("");
  const [selectedDoctorName, setSelectedDoctorName] = useState("");

  // États pour la gestion des erreurs
  const [errors, setErrors] = useState<
    Partial<Record<keyof ConstanteFormData, string>>
  >({});
  const [formError, setFormError] = useState<string | null>(null);

  // Chargement initial des données
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.USERS.DOCTORS.SORTED);
        setDoctors(res.data);
      } catch (error) {
        console.error("Erreur lors du chargement des docteurs :", error);
        setFormError("Erreur lors du chargement des docteurs");
      }
    };

    const fetchPatients = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.PATIENTS.LIST);
        setPatients(res.data);
        setFilteredPatients(res.data);
      } catch (error) {
        console.error("Erreur lors du chargement des patients :", error);
        setFormError("Erreur lors du chargement des patients");
      }
    };

    fetchDoctors();
    fetchPatients();
  }, []);

  // Mise à jour des informations patient quand sélectionné
  useEffect(() => {
    if (constanteFormData.maladeId && patients.length > 0) {
      const selectedPatient = patients.find(
        (patient) => patient.id.toString() === constanteFormData.maladeId
      );
      if (selectedPatient) {
        setSelectedPatientName(
          `${selectedPatient.firstName} ${selectedPatient.lastName}`
        );
        setSelectedPatientPhone(selectedPatient.phone);
        setSelectedPatientDOB(
          selectedPatient.dateOfBirth
            ? new Date(selectedPatient.dateOfBirth).toLocaleDateString("fr-FR")
            : ""
        );
      }
    }
  }, [constanteFormData.maladeId, patients]);

  // Mise à jour des informations docteur quand sélectionné
  useEffect(() => {
    if (constanteFormData.doctorId && doctors.length > 0) {
      const selectedDoctor = doctors.find(
        (doctor) => doctor.id.toString() === constanteFormData.doctorId
      );
      if (selectedDoctor) {
        setSelectedDoctorName(
          `${selectedDoctor.firstName || ""} ${selectedDoctor.lastName || ""
            }`.trim()
        );
      }
    }
  }, [constanteFormData.doctorId, doctors]);

  // Initialisation des données en mode update
  useEffect(() => {
    if (
      mode === "update" &&
      initialData &&
      patients.length > 0 &&
      doctors.length > 0
    ) {
      // Récupérer les informations du patient
      const patient = patients.find((p) => p.id === initialData.maladeId);
      if (patient) {
        setSelectedPatientName(`${patient.firstName} ${patient.lastName}`);
        setSelectedPatientPhone(patient.phone);
        setSelectedPatientDOB(
          patient.dateOfBirth
            ? new Date(patient.dateOfBirth).toLocaleDateString("fr-FR")
            : ""
        );
      }

      // Récupérer les informations du docteur
      const doctor = doctors.find((d) => d.id === initialData.doctorId);
      if (doctor) {
        setSelectedDoctorName(
          `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim()
        );
      }
    }
  }, [mode, initialData, patients, doctors]);

  const validateForm = (data: ConstanteFormData) => {
    const newErrors: Partial<Record<keyof ConstanteFormData, string>> = {};

    if (data.poids && !/^\d+(\.\d{1,2})?$/.test(data.poids))
      newErrors.poids = "Poids doit être un nombre (ex: 70 ou 70.5)";

    if (data.taille && !/^\d+(\.\d{1,2})?$/.test(data.taille))
      newErrors.taille = "Taille doit être un nombre (ex: 175 ou 175.5)";

    if (
      data.tensionArterielle &&
      !/^\d{1,3}\/\d{1,3}$/.test(data.tensionArterielle)
    )
      newErrors.tensionArterielle =
        "Tension doit être au format systolique/diastolique (ex: 120/80)";

    if (data.frequenceCardiaque && !/^\d+$/.test(data.frequenceCardiaque))
      newErrors.frequenceCardiaque =
        "Fréquence cardiaque doit être un nombre entier";

    if (data.temperature && !/^\d+(\.\d{1,2})?$/.test(data.temperature))
      newErrors.temperature =
        "Température doit être un nombre (ex: 37 ou 37.2)";

    if (!data.motif) newErrors.motif = "Motif est requis";

    if (role === "admin" && !data.doctorId)
      newErrors.doctorId = "Docteur est requis";

    if (!data.maladeId) newErrors.maladeId = "Patient est requis";

    if (mode === "update" && !data.status)
      newErrors.status = "Statut est requis";

    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setConstanteFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormError(null);
  };

  const handleSearchPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchPhone(value);
    const filtered = patients.filter((patient) =>
      patient.phone.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredPatients(filtered);
  };

  const handleResetFilter = () => {
    setSearchPhone("");
    setFilteredPatients(patients);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm(constanteFormData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      await onSubmit(constanteFormData);
    } catch (err) {
      console.error("Error submitting constante:", err);
      setFormError("Erreur lors de la soumission des constantes");
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 w-full max-w-5xl animate-slideInUp">
      {formError && (
        <div className="mb-4 text-sm text-red-500">{formError}</div>
      )}

      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {mode === "create"
                ? "Créer une constante"
                : "Mettre à jour la constante"}
            </h1>
            <p className="text-gray-600 text-sm">
              {mode === "create"
                ? "Enregistrez les constantes vitales du patient."
                : "Modifiez les constantes vitales du patient."}
            </p>
          </div>
        </div>
        <Link
          to={`/${role}/dashboard/constante`}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors shadow-md"
          aria-label="Retour à la gestion des constantes"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Retour</span>
        </Link>
      </div>

      {role === "admin" && (
        <SearchPhoneInput
          searchPhone={searchPhone}
          onSearchChange={handleSearchPhone}
          onReset={handleResetFilter}
          className="mb-6"
        />
      )}

      <form
        onSubmit={handleFormSubmit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-6"
      >
        {/* Sélection du Patient */}
        <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="maladeId"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Patient <span className="text-red-500">*</span>
            </label>
            <select
              id="maladeId"
              name="maladeId"
              value={constanteFormData.maladeId}
              onChange={handleChange}
              disabled={mode === "update"}
              className={`w-full px-4 py-2.5 border ${errors.maladeId ? "border-red-500" : "border-gray-200"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors`}
            >
              <option value="">-- Sélectionner un patient --</option>
              {(role === "admin" ? filteredPatients : patients).map(
                (patient) => (
                  <option key={patient.id} value={patient.id}>
                    {role === "admin"
                      ? `${patient.phone} - ${patient.firstName} ${patient.lastName}`
                      : `${patient.firstName} ${patient.lastName} - ${patient.phone}`}
                  </option>
                )
              )}
            </select>
            {errors.maladeId && (
              <p className="mt-1 text-sm text-red-500">{errors.maladeId}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="patientName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nom complet du patient
            </label>
            <input
              type="text"
              id="patientName"
              value={selectedPatientName}
              disabled
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-800 cursor-not-allowed transition-all"
            />
          </div>
        </div>

        {/* Informations du patient (affichage) */}
        {selectedPatientName && (
          <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="patientPhone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Téléphone du patient
              </label>
              <input
                type="text"
                id="patientPhone"
                value={selectedPatientPhone}
                disabled
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-800 cursor-not-allowed transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="patientDOB"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Date de naissance
              </label>
              <input
                type="text"
                id="patientDOB"
                value={selectedPatientDOB}
                disabled
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-800 cursor-not-allowed transition-all"
              />
            </div>
          </div>
        )}

        {/* Constantes vitales */}
        <div>
          <label
            htmlFor="poids"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Poids (kg)
          </label>
          <input
            type="text"
            id="poids"
            name="poids"
            value={constanteFormData.poids}
            onChange={handleChange}
            placeholder="Ex: 70"
            className={`w-full px-4 py-2.5 border ${errors.poids ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.poids && (
            <p className="mt-1 text-sm text-red-500">{errors.poids}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="taille"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Taille (cm)
          </label>
          <input
            type="text"
            id="taille"
            name="taille"
            value={constanteFormData.taille}
            onChange={handleChange}
            placeholder="Ex: 175"
            className={`w-full px-4 py-2.5 border ${errors.taille ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.taille && (
            <p className="mt-1 text-sm text-red-500">{errors.taille}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="tensionArterielle"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Tension artérielle
          </label>
          <input
            type="text"
            id="tensionArterielle"
            name="tensionArterielle"
            value={constanteFormData.tensionArterielle}
            onChange={handleChange}
            placeholder="Ex: 120/80"
            className={`w-full px-4 py-2.5 border ${errors.tensionArterielle ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.tensionArterielle && (
            <p className="mt-1 text-sm text-red-500">
              {errors.tensionArterielle}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="frequenceCardiaque"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Fréquence cardiaque (bpm)
          </label>
          <input
            type="text"
            id="frequenceCardiaque"
            name="frequenceCardiaque"
            value={constanteFormData.frequenceCardiaque}
            onChange={handleChange}
            placeholder="Ex: 72"
            className={`w-full px-4 py-2.5 border ${errors.frequenceCardiaque ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.frequenceCardiaque && (
            <p className="mt-1 text-sm text-red-500">
              {errors.frequenceCardiaque}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="temperature"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Température (°C)
          </label>
          <input
            type="text"
            id="temperature"
            name="temperature"
            value={constanteFormData.temperature}
            onChange={handleChange}
            placeholder="Ex: 37.2"
            className={`w-full px-4 py-2.5 border ${errors.temperature ? "border-red-500" : "border-gray-200"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-all`}
          />
          {errors.temperature && (
            <p className="mt-1 text-sm text-red-500">{errors.temperature}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="motif"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Motif <span className="text-red-500">*</span>
          </label>
          {mode === "create" ? (
            <select
              id="motif"
              name="motif"
              value={constanteFormData.motif}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 border ${errors.motif ? "border-red-500" : "border-gray-200"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors`}
            >
              <option value="">-- Sélectionner un motif --</option>
              {Object.values(VisitReason).map((reason) => (
                <option key={reason} value={reason}>
                  {reasonMap[reason]}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="motif"
              name="motif"
              value={
                reasonMap[constanteFormData.motif as VisitReason] ||
                constanteFormData.motif
              }
              disabled
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-800 cursor-not-allowed transition-all"
            />
          )}
          {errors.motif && (
            <p className="mt-1 text-sm text-red-500">{errors.motif}</p>
          )}
        </div>

        {/* Sélection du Docteur (pour admin) */}
        {role === "admin" && (
          <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="doctorId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Docteur <span className="text-red-500">*</span>
              </label>
              <select
                id="doctorId"
                name="doctorId"
                value={constanteFormData.doctorId}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 border ${errors.doctorId ? "border-red-500" : "border-gray-200"
                  } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors`}
              >
                <option value="">-- Sélectionner un docteur --</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.firstName} {doctor.lastName}
                  </option>
                ))}
              </select>
              {errors.doctorId && (
                <p className="mt-1 text-sm text-red-500">{errors.doctorId}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="doctorName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Nom complet du docteur
              </label>
              <input
                type="text"
                id="doctorName"
                value={selectedDoctorName}
                disabled
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-gray-800 cursor-not-allowed transition-all"
              />
            </div>
          </div>
        )}

        {/* Statut (en mode update) */}
        {mode === "update" && (
          <div className="col-span-2">
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Statut <span className="text-red-500">*</span>
            </label>
            <select
              id="status"
              name="status"
              value={constanteFormData.status}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 border ${errors.status ? "border-red-500" : "border-gray-200"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 transition-colors max-w-md`}
            >
              <option value="PENDING">En attente</option>
              <option value="APPROVED">Approuvé</option>
              <option value="REJECTED">Rejeté</option>
              <option value="CANCELLED">Annulé</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-500">{errors.status}</p>
            )}
          </div>
        )}

        {/* Boutons d'action */}
        <div className="col-span-2 flex justify-end gap-3 mt-6">
          <Link
            to={`/${role}/dashboard/constante`}
            className="px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700 hover:bg-gray-100 text-sm font-medium"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg text-sm font-medium ${isSubmitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:from-green-600 hover:to-blue-700"
              } flex items-center justify-center`}
            aria-label={
              mode === "create"
                ? "Créer la constante"
                : "Enregistrer les modifications"
            }
          >
            {isSubmitting ? (
              <>
                <Activity className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                {mode === "create" ? "Enregistrer" : "Mettre à jour"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConstanteFormBase;
